import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Divider,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ShareIcon from "@mui/icons-material/Share";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import UserDialog from "../../components/UserDialog";
import ShareFeedbackModal from "../../components/ShareFeedbackModal";
import {
  getAllBusinesses,
  deleteBusiness,
  createBusiness,
  updateBusiness,
} from "../../services/adminService";
import { useMessage } from "../../contexts/MessageContext";
import { formatDate } from "../../utils/formatDate";

const UserManagement = () => {
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareableLink, setShareableLink] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessToDelete, setBusinessToDelete] = useState(null);
  const { showMessage } = useMessage();

  const loadBusinesses = useCallback(async () => {
    try {
      const data = await getAllBusinesses();
      setBusinesses(data);
    } catch (error) {
      showMessage("Failed to fetch businesses.", "error");
    }
  }, [showMessage]);

  useEffect(() => {
    loadBusinesses();
  }, [loadBusinesses]);

  const handleDelete = async () => {
    try {
      await deleteBusiness(businessToDelete);
      await loadBusinesses();
      setShowDeleteDialog(false);
      showMessage("Business deleted successfully.", "success");
    } catch (error) {
      showMessage("Failed to delete business.", "error");
    }
  };

  const openUserDialog = (business) => {
    setSelectedBusiness(business);
    setShowUserDialog(true);
  };

  const closeUserDialog = () => {
    setSelectedBusiness(null);
    setShowUserDialog(false);
  };

  const handleUserSave = async (formData) => {
    try {
      if (formData.id) {
        const { id, ...updateData } = formData;
        await updateBusiness(id, updateData);
        showMessage("Business updated successfully.", "success");
      } else {
        await createBusiness(formData);
        showMessage("Business created successfully.", "success");
      }
      await loadBusinesses();
      closeUserDialog();
    } catch (error) {
      showMessage(
        error.response?.data?.message || "Failed to save business.",
        "error"
      );
    }
  };

  const handleOpenShareModal = (business) => {
    const frontendBaseURL = window.location.origin;
    setShareableLink(`${frontendBaseURL}/feedback/${business.business_token}`);
    setBusinessName(business.name);
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    setShareableLink("");
    setBusinessName("");
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            Business Management
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Manage business owners by adding, editing, or deleting their
            details.
          </Typography>
        </Box>
        <IconButton
          color="primary"
          onClick={() => openUserDialog(null)}
          sx={{
            fontSize: "1rem",
            backgroundColor: "primary.main",
            color: "#fff",
            "&:hover": { backgroundColor: "primary.dark" },
          }}
        >
          <AddIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Grid container spacing={3}>
        {businesses.map((business) => (
          <Grid item xs={12} sm={6} md={4} key={business.id}>
            <Card
              component={Paper}
              elevation={6}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                minHeight: "350px", // Ensure consistent height
                width: "100%",
                textAlign: "left",
                borderRadius: "12px",
                backgroundColor: "#fff",
                overflow: "hidden",
                boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.2)",
                },
              }}
            >
              <CardContent
                sx={{
                  padding: "16px 24px",
                  flexGrow: 1, // Ensure consistent spacing between content and divider
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    mb: 2,
                    color: "primary.main",
                    fontSize: "1.25rem",
                    textTransform: "capitalize",
                  }}
                >
                  {business.name}
                </Typography>

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <EmailIcon
                    sx={{ color: "text.secondary", fontSize: "1.2rem" }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.95rem" }}
                  >
                    {business.email}
                  </Typography>
                </Box>

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <PhoneIcon
                    sx={{ color: "text.secondary", fontSize: "1.2rem" }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.95rem" }}
                  >
                    {business.phone || "N/A"}
                  </Typography>
                </Box>

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <LocationOnIcon
                    sx={{ color: "text.secondary", fontSize: "1.2rem" }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.95rem" }}
                  >
                    {business.address || "N/A"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarTodayIcon
                    sx={{ color: "text.secondary", fontSize: "1.2rem" }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.95rem" }}
                  >
                    {formatDate(business.created_at)}
                  </Typography>
                </Box>
              </CardContent>

              {/* Ensure the Divider is consistent across all cards */}
              <Divider sx={{ marginX: "16px" }} />

              <CardActions
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  padding: "8px 16px",
                }}
              >
                <IconButton
                  color="primary"
                  onClick={() => openUserDialog(business)}
                  sx={{
                    fontSize: "1.2rem",
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => {
                    setBusinessToDelete(business.id);
                    setShowDeleteDialog(true);
                  }}
                  sx={{
                    fontSize: "1.2rem",
                  }}
                >
                  <DeleteIcon />
                </IconButton>
                <IconButton
                  color="primary"
                  onClick={() => handleOpenShareModal(business)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    fontSize: "0.8rem",
                    color: "primary.dark",
                  }}
                >
                  <ShareIcon sx={{ fontSize: "1.2rem", mb: 0.5 }} />
                  <Typography variant="caption">Share</Typography>
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      {showUserDialog && (
        <UserDialog
          open={showUserDialog}
          onClose={closeUserDialog}
          onSave={handleUserSave}
          business={selectedBusiness}
        />
      )}

      {showDeleteDialog && (
        <ConfirmationDialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={handleDelete}
          title="Confirm Deletion"
          message="Are you sure you want to delete this business?"
          confirmButtonText="Delete"
        />
      )}

      {showShareModal && (
        <ShareFeedbackModal
          open={showShareModal}
          onClose={handleCloseShareModal}
          shareableLink={shareableLink}
          businessName={businessName}
        />
      )}
    </Box>
  );
};

export default UserManagement;
